"use client";

import Script from "next/script";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useConsent, useHasConsent } from "@/lib/consent/hooks";
import { PRIOR_CONSENT_COUNTRIES } from "@/lib/consent/region";
import { useLegitimateInterest } from "@/lib/consent/region-context";

const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;
const GA4_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;

const VALID_META_ID = /^[0-9]{6,20}$/;
const VALID_GA4_ID = /^G-[A-Z0-9]{6,12}$/;

const metaPixelEnabled = !!META_PIXEL_ID && VALID_META_ID.test(META_PIXEL_ID);
const ga4Enabled =
	!!GA4_MEASUREMENT_ID && VALID_GA4_ID.test(GA4_MEASUREMENT_ID);

/**
 * Google Consent Mode v2 region list: the EU/EEA, UK (+ Crown dependencies)
 * and Switzerland, i.e. exactly the countries where `requiresPriorConsent()`
 * says analytics may not run before opt-in. Serialized into the inline consent
 * bootstrap so the DENIED defaults apply there and only there — the page HTML
 * itself stays geo-free and edge-cacheable (the visitor's own country is never
 * rendered into it).
 */
const CONSENT_MODE_REGIONS = JSON.stringify([...PRIOR_CONSENT_COUNTRIES]);

/**
 * gtag.js now loads for EVERY visitor, not only after "marketing" consent.
 * Google Consent Mode v2 carries the permission state instead of the script tag
 * doing it: with `analytics_storage: 'denied'` gtag sends cookieless pings that
 * set no identifier, which is what the prior-consent regions require, and the
 * state is upgraded via `consent update` the moment the region resolves or the
 * visitor answers the banner.
 *
 * Why the tag used to be gated and no longer is: gating the tag meant ~98.6% of
 * visitors (36,195 banner impressions vs 460 accepts / 258 rejects over the 30
 * days to 2026-08-11) were invisible to GA4 — the banner is ignored, not
 * refused — so GA4 measured ~1% of the site.
 *
 * Defaults, deliberately asymmetric:
 * - `analytics_storage` starts DENIED everywhere and is granted below on the
 *   same basis PostHog capture already uses (`posthog-provider.tsx`:
 *   `analyticsAllowed || legitimateInterestOk === true`).
 * - The ad signals (`ad_storage`, `ad_user_data`, `ad_personalization`) are
 *   declared denied ONLY for `CONSENT_MODE_REGIONS`. They are intentionally
 *   left unspecified elsewhere: an unspecified signal keeps Google's
 *   pre-consent-mode behaviour, so the AdSense loader in
 *   `src/app/[locale]/(public)/layout.tsx` — which is not consent-gated today —
 *   keeps serving exactly as it does now outside those regions. Declaring them
 *   denied globally would silently downgrade every ad impression on the site to
 *   non-personalized.
 */
function consentBootstrap(measurementId: string): string {
	return [
		"window.dataLayer=window.dataLayer||[];",
		"function gtag(){dataLayer.push(arguments);}",
		"window.gtag=gtag;",
		`gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',wait_for_update:500,region:${CONSENT_MODE_REGIONS}});`,
		"gtag('consent','default',{analytics_storage:'denied',wait_for_update:500});",
		"gtag('js',new Date());",
		`gtag('config','${measurementId}',{anonymize_ip:true});`,
	].join("");
}

export function MarketingScripts() {
	const marketingAllowed = useHasConsent("marketing");
	const { record, hydrated } = useConsent();
	// `null` until `/api/privacy-region` answers; `true` = outside the
	// prior-consent zone. Both apex and the seeker zone mount
	// `<ConsentRegionProvider>`; anywhere it is absent this stays `null` and
	// analytics_storage is upgraded on explicit consent alone — fail closed,
	// never the other way round.
	const legitimateInterestOk = useLegitimateInterest();
	const posthog = usePostHog();
	/** Last consent signature captured, so the re-runs this effect legitimately
	 *  performs (hydration flip, then region resolution) emit one event, not
	 *  three. */
	const lastConsentSignalRef = useRef<string | null>(null);

	// Consent Mode updates. The explicit answer wins in every region once it
	// exists: a visitor who rejected is honoured even outside the prior-consent
	// zone (stricter than the PostHog gate, which keeps capturing there). Before
	// any answer, the legitimate-interest basis applies exactly as it does for
	// product analytics.
	useEffect(() => {
		if (!ga4Enabled || !hydrated) return;

		const analyticsGranted = record
			? record.choices.analytics === true
			: legitimateInterestOk === true;
		const marketingGranted = record?.choices.marketing === true;

		const payload = {
			analytics_storage: analyticsGranted ? "granted" : "denied",
			ad_storage: marketingGranted ? "granted" : "denied",
			ad_user_data: marketingGranted ? "granted" : "denied",
			ad_personalization: marketingGranted ? "granted" : "denied",
		} as const;

		// This effect used to `return` when `window.gtag` was undefined. In
		// practice it is defined — the `<Script id="ga4-init">` below is a CHILD
		// of this component, and React runs child effects before the parent's,
		// so the inline bootstrap has already installed the shim by the time
		// this runs (verified on production 2026-08-11: `typeof window.gtag ===
		// "function"` and dataLayer carries the `consent update`).
		//
		// It was still the wrong failure mode. That ordering is an
		// implementation detail of next/script's `afterInteractive` strategy and
		// React's effect order, and if either ever changes, the bail is SILENT:
		// consent stays at the `denied` default, GA4 goes back to measuring
		// almost nothing, and the only symptom is a number in a dashboard nobody
		// is watching — which is exactly the bug #3436 was written to fix,
		// recurring with no signal.
		//
		// So install the shim ourselves instead of giving up. It is the same two
		// lines `consentBootstrap` uses, and pushing an `arguments` object (not
		// a plain array) is what makes gtag.js replay it correctly once the
		// loader arrives.
		const w = window as Window & {
			gtag?: (...args: unknown[]) => void;
			dataLayer?: unknown[];
		};
		const hadGtag = typeof w.gtag === "function";
		if (!hadGtag) {
			w.dataLayer = w.dataLayer || [];
			w.gtag = function gtag() {
				// biome-ignore lint/complexity/noArguments: gtag.js only replays `arguments` objects from dataLayer — a plain array is not processed as a command, so rest params would silently break the replay.
				w.dataLayer?.push(arguments);
			};
		}
		w.gtag?.("consent", "update", payload);

		// Telemetry so this path is observable at all. Without it, the failure
		// above stays invisible: nothing in PostHog distinguishes "consent
		// update sent, granted" from "the effect never ran". `via` names which
		// branch fired, so a shift to `bootstrap_missing` in the data IS the
		// early warning that the ordering assumption above has broken.
		//
		// Deduped against the last payload because the effect legitimately
		// re-runs as `hydrated` flips and the region resolves; without this the
		// event would double-count on most page loads.
		const signature = `${payload.analytics_storage}|${payload.ad_storage}|${hadGtag}`;
		if (lastConsentSignalRef.current !== signature) {
			lastConsentSignalRef.current = signature;
			posthog?.capture("consent_mode_updated", {
				analytics_storage: payload.analytics_storage,
				ad_storage: payload.ad_storage,
				has_explicit_record: Boolean(record),
				legitimate_interest_ok: legitimateInterestOk,
				via: hadGtag ? "bootstrap" : "bootstrap_missing",
			});
		}
	}, [record, hydrated, legitimateInterestOk, posthog]);

	if (!metaPixelEnabled && !ga4Enabled) return null;
	// Client-only, deliberately: these tags carry no markup a reader needs, and
	// rendering them during SSR puts inline <script> text into the hydration
	// diff for every public page. `hydrated` is false on the server and on the
	// first client render, so both sides agree on "nothing", and the tags mount
	// one tick later with the consent state already resolved.
	if (!hydrated) return null;

	return (
		<>
			{/* Meta has no Consent Mode equivalent — the pixel stays fully gated. */}
			{metaPixelEnabled && marketingAllowed ? (
				<Script
					id="meta-pixel"
					strategy="afterInteractive"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: Meta's official pixel snippet must be inlined.
					dangerouslySetInnerHTML={{
						__html: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];if(s&&s.parentNode){s.parentNode.insertBefore(t,s)}else{b.head.appendChild(t)}}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${META_PIXEL_ID}');fbq('track','PageView');`,
					}}
				/>
			) : null}
			{ga4Enabled ? (
				<>
					{/* Declared BEFORE the loader: the inline script runs the moment it
					    is injected, the loader is async, so the consent defaults are
					    always in `dataLayer` before gtag.js reads it. */}
					<Script
						id="ga4-init"
						strategy="afterInteractive"
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Google's recommended gtag + consent bootstrap must be inlined.
						dangerouslySetInnerHTML={{
							__html: consentBootstrap(GA4_MEASUREMENT_ID as string),
						}}
					/>
					<Script
						id="ga4-loader"
						strategy="afterInteractive"
						src={`https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`}
					/>
				</>
			) : null}
		</>
	);
}
